import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { useGetDocumentsQuery } from "documents/_api/document-api";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import { useGetForumsQuery } from "messaging/_api/messaging-api";
import ForumQuery, { ForumFilterParameters, ForumOrderField, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import { NAME_SEARCH_LIMIT } from "permissions/_views/people/person-record-grants-model";
import type { RecordNameSearch } from "permissions/_views/people/use-record-name-search";
import { ResourceType } from "permissions/resources/permission-resource-types";
import { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import { ProposalBoxType, ProposalContext, ProposalOrderField } from "work/proposals/__constants/proposal-lifecycle";
import { useGetProposalsQuery } from "work/proposals/_api/proposal-api";
import ProposalQuery, { ProposalFilterParameters, ProposalOrderParameters } from "work/proposals/_api/queries/proposal-query";
import { isProposalLoadIssue } from "work/proposals/_store/proposals-redux-slice";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import { useGetWorkAgreementsQuery } from "work/work-agreements/_api/work-agreement-api";
import { isWorkAgreementLoadIssue } from "work/work-agreements/_store/work-agreement-redux-slice";

/** The kinds whose records have a domain search to find them by name. */
export const READABLE_SEARCH_RECORD_TYPES: ReadonlySet<string> = new Set([
  ResourceType.DocumentAccess,
  ResourceType.ForumAccess,
  ResourceType.ProposalAccess,
  ResourceType.WorkAgreementAccess,
]);

function page() {
  return PaginationParameters.create({ pageIndex: 0, pageSize: NAME_SEARCH_LIMIT });
}

/**
 * Finds records by name through each record's own domain search — the Permissions API has no name
 * search, and names live in the business centers. The person page then asks for the person's grants
 * on just those ids, with the generic `targetIds` filter.
 *
 * What it cannot find, and so what an empty answer does not prove:
 *
 * - a record the viewer may not read, since a domain search answers only those. Its grant is still
 *   on the unfiltered list, where its row says why it has no name;
 * - a match past the newest {@link NAME_SEARCH_LIMIT};
 * - for proposals, one outside the active box (`AllActive`: no drafts, nothing archived) or on the
 *   other side from the one the viewer acts as.
 *
 * This is the search of a member without Member Permissions, on their own permissions page: the
 * admin-gated targets search (#6174), which lifts these limits, refuses them (#6795 review).
 */
export default function useReadableRecordNameSearch(resourceType: string | undefined, text: string): RecordNameSearch {
  const session = useSession();
  const query = text.trim() || undefined;
  const asVendor = session.context?.viewingAsVendor ?? false;

  // Memoized on purpose: each query is a class instance that reaches RTK Query's arguments.
  const documentQuery = useMemo(
    () =>
      resourceType === ResourceType.DocumentAccess && query
        ? new DocumentQuery({
            filterParameters: new DocumentFilterParameters({ query }),
            orderParameters: new DocumentOrderParameters({ orderBy: DocumentOrderField.Created, orderDir: OrderDirection.Descending }),
            paginationParameters: page(),
          })
        : undefined,
    [resourceType, query],
  );
  const forumQuery = useMemo(
    () =>
      resourceType === ResourceType.ForumAccess && query
        ? new ForumQuery({
            filterParameters: new ForumFilterParameters({ query }),
            orderParameters: new ForumOrderParameters({ orderBy: ForumOrderField.CreatedDate, orderDir: OrderDirection.Descending }),
            paginationParameters: page(),
          })
        : undefined,
    [resourceType, query],
  );
  const proposalQuery = useMemo(
    () =>
      resourceType === ResourceType.ProposalAccess && query
        ? new ProposalQuery({
            filterParameters: new ProposalFilterParameters({
              query,
              context: asVendor ? ProposalContext.Vendor : ProposalContext.Client,
              boxType: ProposalBoxType.AllActive,
            }),
            orderParameters: new ProposalOrderParameters({ orderBy: ProposalOrderField.Created, orderDir: OrderDirection.Descending }),
            paginationParameters: page(),
          })
        : undefined,
    [resourceType, query, asVendor],
  );
  const workAgreementQuery = useMemo(
    () =>
      resourceType === ResourceType.WorkAgreementAccess && query
        ? WorkAgreementQuery.create({
            filterParameters: WorkAgreementFilterParameters.create({ query }),
            orderParameters: WorkAgreementOrderParameters.create({
              orderBy: WorkAgreementOrderField.Start,
              orderDirection: OrderDirection.Descending,
            }),
            paginationParameters: page(),
          })
        : undefined,
    [resourceType, query],
  );

  const documents = useGetDocumentsQuery(documentQuery ? { query: documentQuery } : skipToken);
  const forums = useGetForumsQuery(forumQuery ? { query: forumQuery } : skipToken);
  const proposals = useGetProposalsQuery(proposalQuery ? { interfaceName: "permissionsRecordSearch", query: proposalQuery } : skipToken);
  const workAgreements = useGetWorkAgreementsQuery(
    workAgreementQuery ? { interfaceName: "permissionsRecordSearch", query: workAgreementQuery } : skipToken,
  );

  const ids = useMemo(() => {
    if (documentQuery) return documents.data?.data.map((document) => document.id.value);
    if (forumQuery) return forums.data?.map((forum) => String(forum.id));
    if (proposalQuery) return proposals.data?.data.flatMap((item) => (isProposalLoadIssue(item) ? [] : [String(item.id)]));
    if (workAgreementQuery)
      return workAgreements.data?.data.flatMap((item) => (isWorkAgreementLoadIssue(item) ? [] : [String(item.id)]));
    return undefined;
  }, [documentQuery, documents.data, forumQuery, forums.data, proposalQuery, proposals.data, workAgreementQuery, workAgreements.data]);

  const active = documentQuery ? documents : forumQuery ? forums : proposalQuery ? proposals : workAgreementQuery ? workAgreements : undefined;
  return {
    ids,
    isSearching: active ? active.isFetching : false,
    error: active?.error ? (getErrorMessageFromUnknown(active.error) ?? "The search failed.") : undefined,
  };
}
